import { useEffect, useRef } from "react";
import type { NoteType } from "@/lib/types";

/**
 * Tone chip radiogroup (M7.5 B1 + B2) — replaces the M4
 * `<CanvasSelect>` flatten of legacy's chip radiogroup. Ports
 * `js/coaching/review.js` (tone-chip render 2125–2131,
 * roving-tabindex 2245–2350).
 *
 * Five chips matching the legacy keys + labels + glyphs. WAI-ARIA
 * `role="radiogroup"`, each chip `role="radio"` with the
 * roving-tabindex pattern (active = 0, others = -1).
 *
 * Keyboard:
 *   - ArrowLeft / ArrowUp   → previous chip (wraps)
 *   - ArrowRight / ArrowDown → next chip (wraps)
 *   - Home → first
 *   - End  → last
 *   - Space / Enter → activate the focused chip (also fires on
 *     click). Automatic activation on arrow-move per ARIA
 *     authoring-practices "radio" pattern.
 *
 * Mirrors the same pattern the M6a `CoachTabStrip` uses for its
 * sub-tab strip — one source of truth across the app.
 */

type ToneOption = { value: NoteType; label: string; glyph: string };

/** Legacy `NOTE_TYPES` order + glyphs from
 * `js/coaching/review.js:2125-2131`. */
const TONES: ReadonlyArray<ToneOption> = [
  { value: "positive", label: "Positive", glyph: "+" },
  { value: "correction", label: "Correction", glyph: "↺" },
  { value: "question", label: "Question", glyph: "?" },
  { value: "team_concept", label: "Team", glyph: "⌬" },
  { value: "individual_goal", label: "Goal", glyph: "★" },
];

export type ToneChipGroupProps = {
  value: NoteType;
  onChange: (next: NoteType) => void;
  "data-testid"?: string;
};

export function ToneChipGroup({
  value,
  onChange,
  "data-testid": testid = "composer-note-type",
}: ToneChipGroupProps) {
  const ref = useRef<HTMLDivElement>(null);
  // Track the *focused* index separately from the *selected* value
  // so arrow keys move focus, the move fires `onChange`
  // (automatic activation), and the chip's tabindex stays in sync.
  const activeIndex = Math.max(
    0,
    TONES.findIndex((t) => t.value === value),
  );

  // Bring focus to the active chip when the group already owns
  // focus (e.g. after an arrow-move). Without this, the
  // tabindex=0 chip moves but the focused element doesn't follow.
  useEffect(() => {
    if (!ref.current) return;
    const owns = ref.current.contains(document.activeElement);
    if (!owns) return;
    const btn = ref.current.querySelector<HTMLButtonElement>(
      `[data-tone-index="${activeIndex}"]`,
    );
    btn?.focus();
  }, [activeIndex]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      e.key !== "ArrowLeft" &&
      e.key !== "ArrowRight" &&
      e.key !== "ArrowUp" &&
      e.key !== "ArrowDown" &&
      e.key !== "Home" &&
      e.key !== "End"
    ) {
      return;
    }
    e.preventDefault();
    let next = activeIndex;
    if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      next = (activeIndex - 1 + TONES.length) % TONES.length;
    } else if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      next = (activeIndex + 1) % TONES.length;
    } else if (e.key === "Home") {
      next = 0;
    } else if (e.key === "End") {
      next = TONES.length - 1;
    }
    onChange(TONES[next].value);
  };

  return (
    <div
      ref={ref}
      role="radiogroup"
      aria-label="Note tone"
      data-testid={testid}
      onKeyDown={onKeyDown}
      className="flex flex-wrap gap-1"
    >
      {TONES.map((tone, i) => {
        const selected = i === activeIndex;
        return (
          <button
            key={tone.value}
            type="button"
            role="radio"
            aria-checked={selected}
            data-tone-index={i}
            data-tone={tone.value}
            data-active={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(tone.value)}
            className={[
              "rounded px-2 py-1 text-xs font-display uppercase tracking-wide transition-colors",
              "flex items-center gap-1.5",
              selected
                ? "bg-accent text-white"
                : "bg-surface text-ink-muted hover:text-ink",
            ].join(" ")}
          >
            <span aria-hidden="true" className="text-sm leading-none">
              {tone.glyph}
            </span>
            <span>{tone.label}</span>
          </button>
        );
      })}
    </div>
  );
}
